import { I18N } from "aurelia-i18n";
import { bindable } from "aurelia-templating";
import { Guid } from '../../../../../utilities/guid';
import { autoinject } from "aurelia-dependency-injection";
import { Claims } from '../../../../../constants/authentication/claims';
import { RulesClientService } from '../../../../../services/rules/rules-client.service';
import { DialogServiceConstants } from '../../../../../constants/basics/dialog.constants';
import { EntityTypeConfigurationHelper } from '../../../../../models/basics/basics.models';
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { EntityFinderSearch } from '../../../../components/entity-finder/entity-finder-search';
import { UserClaimsService } from '../../../../../services/authentication/user-claims.service';
import { DataType, ItemSource, QueryFilter } from '../../../../../models/queries/queries.models';
import { RightshipDataGrid } from './../../../../components/rightship-data-grid/rightship-data-grid';
import { RightShipDialogServiceConstants } from '../../../../../constants/basics/rightship-dialog.constants';
import { GenericConfirmationDialogModel } from '../../../../../models/basics/generic-confirmation-dialog-model';
import { NotificationClientService, NotificationType } from '../../../../../services/basics/notification-client.service';
import { Reflection } from "../../../../../utilities/reflection";
import { computedFrom } from "aurelia-binding";

@autoinject
export class RuleCollectionsHeader {

    @bindable
    dataGrid: RightshipDataGrid;
    @bindable
    itemSource: ItemSource;

    private _searchGuidKey = "ruleCollectionFilter_";
    canAddRuleCollections: boolean = false;
    canRemoveRuleCollections: boolean = false;
    canAssignRuleCollections: boolean = false;

    ruleCollectionEntityTypes;
    selectedRuleColection: number;
    rcEfSearchVm: EntityFinderSearch;

    constructor(userClaimsService: UserClaimsService,
        private dialogService: DialogClientService,
        private rulesService: RulesClientService,
        private notificationClientService: NotificationClientService,
        private i18N: I18N,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper) {
        this.canAddRuleCollections = userClaimsService.hasPermission(Claims.CanAddRuleCollections);
        this.canRemoveRuleCollections = userClaimsService.hasPermission(Claims.CanRemoveRuleCollections);
        this.canAssignRuleCollections = userClaimsService.hasPermission(Claims.CanAssignRuleCollections);
        this.ruleCollectionEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["RuleCollections"]);
    }

    @computedFrom(Reflection.getPropertyPath<RuleCollectionsHeader>(z => z.dataGrid),
        Reflection.getPropertyPath<RuleCollectionsHeader>(z => z.dataGrid.itemsCollection),
        Reflection.getPropertyPath<RuleCollectionsHeader>(z => z.dataGrid.itemsCollection.length),
        "dataGrid._specificIds", "dataGrid._specificIds.length")
    get shouldDisableAssign(): boolean {
        return !this.dataGrid || !this.dataGrid.getSelectedRowsCollection() || this.dataGrid.getSelectedRowsCollection().length <= 0;
    }

    addRuleCollection() {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.AddRulesCollectionDialog, {
            dataGrid: this.dataGrid,
            ruleCollectionId: null
        }, true, false, false, 'right');
    }

    openAssignRuleCollections() {
        if (this.shouldDisableAssign)
            return;

        this.dialogService.show(RightShipDialogServiceConstants.URLs.AssignRuleCollectionsDialog, {
            dataGrid: this.dataGrid,
            ids: this.dataGrid.getSpecificIds(),
            allSelected: this.dataGrid.toggleAll,
            filters: this.getFilters()
        }, true, false, false, 'right');
    }

    public getFilters() {
        let filtersToInclude: QueryFilter[] = [];
        if (this.dataGrid.itemSource.columns) {
            this.dataGrid.itemSource.columns.filter(x => x.isFilterable).forEach(x => {
                if (x.filtersResolver) {
                    const filters = x.filtersResolver(x.filters);
                    if (filters) {
                        filtersToInclude = filtersToInclude.concat(filters);
                    }
                } else {
                    if (x.filters) {
                        filtersToInclude = filtersToInclude.concat(x.filters);
                    }
                }
            });
        }

        this.dataGrid.ensureValues();

        const included: {
            [name: string]: QueryFilter[],
        } = {};

        // Remove filters residing both in initialFilters and filtersToInclude.  Gives precedence to filtersToInclude.
        filtersToInclude.filter(x => x.filterProperty).forEach(x => {
            if (!x.filterProperty.name) {
                return;
            }

            (included[x.filterProperty.name] || (included[x.filterProperty.name] = [])).push(x);
        });

        const initialFilters = this.dataGrid.initialFilters.filter(x => x.filterProperty && x.filterValue != null && x.filterValue != undefined
            && !(Array.isArray(x.filterValue) && x.filterValue.length == 0) // remove filters with empty array values
            && !included[x.filterProperty.name]);
        return initialFilters.concat(filtersToInclude);
    }

    showDeleteDialog() {
        if (this.shouldDisableAssign)
            return;

        if ((!this.dataGrid.toggleAll && this.dataGrid.getSpecificIds().length == 0)) {
            this.notificationClientService.show(this.i18N.tr('no-items-selected-text'), "", NotificationType.Warning);
            return;
        }
        const model = new GenericConfirmationDialogModel();
        model.confirm = this.i18N.tr('delete-text');
        model.cancel = this.i18N.tr('cancel-text');
        model.messages.push(this.i18N.tr('are-you-sure-you-want-to-delete'));
        model.title = this.i18N.tr('confirm-delete-text');
        model.onConfirm = () => this.removeRuleCollections();
        this.dialogService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, true, false, false, 'right');
    }

    removeRuleCollections() {
        return new Promise<void>((resolve, reject) => {
            const selectedRuleCollectionIds = this.dataGrid.getSelectedRowsCollection().map(x => x.item.ruleCollectionId);
            this.rulesService.deleteRuleCollections(selectedRuleCollectionIds).then(() => {
                this.dataGrid.reloadData();
                resolve();
            }).catch(() => { reject(); });
        });
    }

    searchRuleCollectionSelected($event) {
        const value = $event && $event.detail && $event.detail.selectedEntity ? $event.detail.selectedEntity.id : null;
        if (this.selectedRuleColection != null || value != null) {
            this.selectedRuleColection = value;

            if (value) {
                const filters = [];
                const guid = this._searchGuidKey + Guid.generateGuid();
                filters.push(QueryFilter.getQuery("dependantRuleCollectionIds", `;${value};`, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));

                this.itemSource.initialFilters = filters;
            }
            else {
                this.itemSource.initialFilters = this.itemSource.initialFilters.filter(x => !x.groupGuid.startsWith(this._searchGuidKey));
            }
        }
    }

    clearSearch() {
        this.selectedRuleColection = null;
        if (this.rcEfSearchVm && this.rcEfSearchVm.removeFilter) {
            this.rcEfSearchVm.removeFilter();
            this.itemSource.initialFilters = this.itemSource.initialFilters.filter(x => !x.groupGuid.startsWith(this._searchGuidKey));
        }
    }
}
